import { useState, useEffect, useRef } from 'react'
import styled from 'styled-components'
import { Icon_Paper } from '../../../styles/icons'
import useTranslation from '../../../hooks/useTranslation'
import useApi from '../../../hooks/useApi'
import PdfView from '../../pdf/PdfView'
import PdfErrorBoundary from '../../pdf/PdfErrorBoundary'
import RichTextDisplay from '../../richText/RichTextDisplay'

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding: 0.5rem;
`

const PillRow = styled.div`
  display: flex;
  gap: 0.5rem;
  overflow-x: auto;
  padding-bottom: 0.25rem;
  flex-wrap: wrap;
`

const Pill = styled.button`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.4rem 0.8rem;
  border-radius: 0.5rem;
  border: 1px solid ${({ $active, theme }) => $active ? theme.btn.primary : theme.form.border};
  background: ${({ $active, theme }) => $active ? theme.dropdown.active : theme.form.background};
  font-size: var(--font-S);
  cursor: pointer;
  white-space: nowrap;
  color: ${({ theme }) => theme.font.color};
  transition: border-color 0.15s, background 0.15s;

  svg { font-size: 1rem; flex-shrink: 0; opacity: 0.7; }
`

const ViewerArea = styled.div`
  width: 100%;
  min-height: 400px;
  border: 1px solid ${({ theme }) => theme.form.border};
  border-radius: 0.5rem;
  overflow: hidden;
  background: ${({ theme }) => theme.form.background};
`

const PdfWrapper = styled.div`
  width: 100%;
  height: ${({ $height }) => `${$height}px`};
  padding: 1rem;
`

const ImageWrapper = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding: 1rem;
`

const PreviewImage = styled.img`
  max-width: 100%;
  border-radius: 6px;
`

const LinkCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding: 1.25rem;
`

const LinkUrl = styled.span`
  font-size: 0.875rem;
  color: ${({ theme }) => theme.font?.subtle || '#6b7280'};
  word-break: break-all;
`

const OpenButton = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  background: ${({ theme }) => theme.color?.primary || '#2563eb'};
  color: #fff;
  font-size: 0.875rem;
  font-weight: 500;
  text-decoration: none;
  align-self: flex-start;
  &:hover { opacity: 0.88; }
`

const RtfToolbar = styled.div`
  display: flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.375rem 0.75rem;
  border-bottom: 1px solid ${({ theme }) => theme.form.border};
`

const FontSizeBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1.5rem;
  height: 1.5rem;
  border-radius: 0.25rem;
  border: 1px solid ${({ theme }) => theme.form.border};
  background: ${({ theme }) => theme.form.background};
  color: ${({ theme }) => theme.font.color};
  font-size: 1rem;
  line-height: 1;
  cursor: pointer;
  flex-shrink: 0;
  &:disabled { opacity: 0.35; cursor: default; }
  &:not(:disabled):hover { background: ${({ theme }) => theme.dropdown.active}; }
`

const FontSizeLabel = styled.span`
  font-size: var(--font-XS);
  color: ${({ theme }) => theme.font.color};
  opacity: 0.65;
  min-width: 2.5rem;
  text-align: center;
`

const StatusMsg = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 200px;
  color: ${({ theme }) => theme.font?.subtle || '#9ca3af'};
  font-size: 0.875rem;
  padding: 2rem;
  text-align: center;
`

const RTF_FONT_STEP = 0.125
const RTF_FONT_MIN  = 0.625
const RTF_FONT_MAX  = 1.5
const PDF_PAD       = 16

const DocumentDisplay = ({ data }) => {
  const { t } = useTranslation()
  const { getMe, getMeStream } = useApi()
  const wrapperRef = useRef(null)

  const documents = data?.documents || []
  const [selectedIdx, setSelectedIdx] = useState(0)
  const [docRecord, setDocRecord] = useState(null)
  const [blob, setBlob] = useState(null)
  const [objectUrl, setObjectUrl] = useState(null)
  const [viewerWidth, setViewerWidth] = useState(600)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [rtfFontSize, setRtfFontSize] = useState(0.875)

  const selected = documents[selectedIdx]

  // Measure viewer width for PDF sizing
  useEffect(() => {
    const update = () => {
      if (wrapperRef.current) setViewerWidth(wrapperRef.current.getBoundingClientRect().width)
    }
    update()
    window.addEventListener('resize', update)
    const obs = new ResizeObserver(update)
    if (wrapperRef.current) obs.observe(wrapperRef.current)
    return () => { window.removeEventListener('resize', update); obs.disconnect() }
  }, [])

  // Fetch full document record when selection changes
  useEffect(() => {
    if (!selected?.id) return
    let cancelled = false
    setDocRecord(null)
    setBlob(null)
    setObjectUrl(null)
    setError(null)
    setLoading(true)

    getMe(`document/${selected.id}`).then(res => {
      if (cancelled) return
      setDocRecord(res?.data || null)
      setLoading(false)
    }).catch(() => {
      if (!cancelled) { setError(t('workExecution.tab.instructions.loadError') || 'Could not load document.'); setLoading(false) }
    })

    return () => { cancelled = true }
  }, [selected?.id])

  // Fetch file blob for PDF / image types
  useEffect(() => {
    if (objectUrl) { URL.revokeObjectURL(objectUrl); setObjectUrl(null) }
    if (!docRecord?.fileUrl || docRecord.fileType === 'link' || docRecord.fileType === 'richtext') return

    let cancelled = false
    getMeStream(`file/${docRecord.fileUrl}?render=document`).then(file => {
      if (cancelled || !file || file.error) return
      setBlob(file)
      setObjectUrl(URL.createObjectURL(file))
    })

    return () => { cancelled = true }
  }, [docRecord?.fileUrl, docRecord?.fileType])

  // Revoke object URL on unmount
  useEffect(() => {
    return () => { if (objectUrl) URL.revokeObjectURL(objectUrl) }
  }, [objectUrl])

  const renderViewer = () => {
    if (!selected) return <StatusMsg>{t('workExecution.tab.instructions.empty') || 'No documents attached.'}</StatusMsg>
    if (loading)   return <StatusMsg>{t('all.loading') || 'Loading...'}</StatusMsg>
    if (error)     return <StatusMsg>{error}</StatusMsg>
    if (!docRecord) return null

    const { fileType, linkUrl, content } = docRecord

    if (fileType === 'link') {
      return (
        <LinkCard>
          {linkUrl && <LinkUrl>{linkUrl}</LinkUrl>}
          <OpenButton href={linkUrl} target="_blank" rel="noreferrer noopener">
            {t('workExecution.tab.instructions.openLink') || 'Open Link'} ↗
          </OpenButton>
        </LinkCard>
      )
    }

    if (fileType === 'richtext') {
      return (
        <div>
          <RtfToolbar>
            <FontSizeBtn
              type="button"
              onClick={() => setRtfFontSize(s => Math.max(RTF_FONT_MIN, +(s - RTF_FONT_STEP).toFixed(3)))}
              disabled={rtfFontSize <= RTF_FONT_MIN}
            >−</FontSizeBtn>
            <FontSizeLabel>{Math.round(rtfFontSize * 16)}px</FontSizeLabel>
            <FontSizeBtn
              type="button"
              onClick={() => setRtfFontSize(s => Math.min(RTF_FONT_MAX, +(s + RTF_FONT_STEP).toFixed(3)))}
              disabled={rtfFontSize >= RTF_FONT_MAX}
            >+</FontSizeBtn>
          </RtfToolbar>
          <div style={{ padding: '1rem' }}>
            <RichTextDisplay value={content} fontSize={`${rtfFontSize}rem`} />
          </div>
        </div>
      )
    }

    if (!objectUrl) return <StatusMsg>{t('all.loading') || 'Loading...'}</StatusMsg>

    if (fileType === 'pdf') {
      const pdfWidth = viewerWidth - PDF_PAD * 2
      return (
        <PdfWrapper $height={pdfWidth * 1.41 + 65 + PDF_PAD * 2}>
          <PdfErrorBoundary>
            <PdfView pdf={blob} width={pdfWidth} />
          </PdfErrorBoundary>
        </PdfWrapper>
      )
    }

    if (fileType === 'image') {
      return (
        <ImageWrapper>
          <PreviewImage src={objectUrl} alt={docRecord.fileName || selected.name} />
        </ImageWrapper>
      )
    }

    return <StatusMsg>{t('workExecution.tab.instructions.unsupported') || 'Preview not available for this file type.'}</StatusMsg>
  }

  if (documents.length === 0) {
    return (
      <Container>
        <StatusMsg>{t('workExecution.tab.instructions.empty') || 'No documents attached.'}</StatusMsg>
      </Container>
    )
  }

  return (
    <Container>
      <PillRow>
        {documents.map((doc, idx) => (
          <Pill
            key={doc.id || idx}
            type="button"
            $active={idx === selectedIdx}
            onClick={() => setSelectedIdx(idx)}
          >
            {Icon_Paper}
            {doc.name}
          </Pill>
        ))}
      </PillRow>
      <ViewerArea ref={wrapperRef}>
        {renderViewer()}
      </ViewerArea>
    </Container>
  )
}

export default DocumentDisplay
